import { AppState } from "@auth0/auth0-react";
import { useAnalytics } from "@codat/analytics";
import history from "history";
import { useGTM } from "hooks/useGTM";
import * as React from "react";
import { useEffect } from "react";
import { Route, Switch, useHistory, useLocation } from "react-router-dom";
import { CompatRoute } from "react-router-dom-v5-compat";

import AcceptInvite from "components/AcceptInvite";
import AuthorizedRoute from "components/AuthorizedRoute";
import AuthProvider from "components/AuthProvider/AuthProvider";
import CompaniesRoutedPage from "components/CompaniesRoutedPage";
import ErrorBoundary from "components/ErrorBoundary";
import Layout from "components/Layout";
import { Onboarding } from "components/Onboarding";
import SingleSignOn from "components/SingleSignOn";
import TrackedRoute from "components/TrackedRoute";
import { ApiKeys } from "pages";
import AccountSettings from "pages/AccountSettings";
import Billing from "pages/Billing";
import { Branding } from "pages/Branding/Branding";
import ClientSelect from "pages/ClientSelect";
import Companies from "pages/Companies";
import { CompanySpendInsights } from "pages/CompanySpendInsights";
import ConnectorHealth from "pages/ConnectorHealth";
import Deprecations from "pages/Deprecations";
import Error from "pages/Error";
import Home from "pages/Home";
import IntegrationsManagement from "pages/IntegrationsManagement";
import LinkSettings from "pages/LinkSettings/LinkSettings";
import NotFound from "pages/NotFound";
import ProductPages from "pages/ProductPages";
import Products from "pages/Products";
import { ProductSettings } from "pages/Products/Settings/ProductSettings";
import { Redirects } from "pages/Redirects/Redirects";
import SettingsOverview from "pages/SettingsOverview";
import { SpendInsights } from "pages/SpendInsights";
import {
  SyncHealthBanking,
  SyncHealthCommerce,
  SyncHealthExpenses,
} from "pages/SyncHealth";
import SynchronizationSettings from "pages/SynchronizationSettings";
import Users from "pages/Users";
import WebhookEvents from "pages/WebhookEvents";
import WebhookRules from "pages/WebhookRules";
import WebhookSettings from "pages/WebhookSettings";

import ManagedBankFeedsSyncs from "../../pages/ManagedBankFeeds";
import { paths } from "./paths";

function AppRouter({ location }: { location?: history.Location }) {
  const thisHistory = useHistory();
  const { setUserProperties } = useAnalytics();
  const loc = useLocation();
  const pathAndHash = loc.pathname + loc.hash;
  const { GA4ClientId, UAClientId } = useGTM();

  if (loc.hash.startsWith("#/")) {
    thisHistory.replace(loc.hash.replace("#", ""));
  } else if (pathAndHash.startsWith("/portal/#!/")) {
    thisHistory.replace(pathAndHash.replace("/portal/#!", ""));
  }

  useEffect(() => {
    if (GA4ClientId) {
      setUserProperties({ ga4ClientId: GA4ClientId });
    }
  }, [GA4ClientId, setUserProperties]);

  useEffect(() => {
    if (UAClientId) {
      setUserProperties({ uaClientId: UAClientId });
    }
  }, [UAClientId, setUserProperties]);
  const onRedirectCallback = (appState: AppState | undefined) => {
    thisHistory.push(appState?.returnTo ?? window.location.pathname);
  };
  return (
    <Switch location={location}>
      {/* These routes need to be outside the <AuthProvider> component, otherwise they will attempt to sign in using OIDC */}
      <Route path={paths.auth.SSO_SIGN_IN} component={SingleSignOn} />
      <Route path={paths.auth.INVITE_ACCEPT} component={AcceptInvite} />
      <AuthProvider onRedirectCallback={onRedirectCallback}>
        <ErrorBoundary FallbackComponent={Error}>
          <Switch>
            <Route exact path={paths.clients.SELECT} component={ClientSelect} />
            <Layout>
              <Route path="/">
                <Switch location={location}>
                  <TrackedRoute
                    exact
                    name="Onboarding"
                    path={paths.ONBOARDING}
                    component={Onboarding}
                  />
                  <TrackedRoute
                    exact
                    path={paths.HOME}
                    name="Home Page"
                    component={Home}
                  />
                  <Route
                    exact
                    path={paths.SPEND_INSIGHTS}
                    component={SpendInsights}
                  />
                  <Route
                    exact
                    path={paths.spendInsights.company.THREADS}
                    component={CompanySpendInsights}
                  />
                  <Route
                    path={paths.spendInsights.company.INSIGHTS}
                    component={CompanySpendInsights}
                  />
                  <Route
                    exact
                    path={paths.products.SYNC_FOR_COMMERCE}
                    component={SyncHealthCommerce}
                  />
                  <Route
                    exact
                    path={paths.products.SYNC_FOR_EXPENSES}
                    component={SyncHealthExpenses}
                  />
                  <Route
                    exact
                    path={paths.products.SYNC_FOR_BANKING}
                    component={SyncHealthBanking}
                  />
                  <TrackedRoute
                    exact
                    path={paths.COMPANIES}
                    name="Companies"
                    component={Companies}
                  />
                  <AuthorizedRoute
                    path={paths.COMPANIES_DATA}
                    permission="data_all_view"
                    component={CompaniesRoutedPage}
                  />
                  <AuthorizedRoute
                    path={paths.monitor.EVENTS}
                    permission="clients_alerts_view"
                    component={WebhookEvents}
                  />
                  <AuthorizedRoute
                    path={paths.settings.RULES}
                    permission="clients_alerts_view"
                    component={WebhookRules}
                  />
                  <AuthorizedRoute
                    path={paths.settings.WEBHOOK_SECURITY}
                    permission="clients_companyProfile_edit"
                    component={WebhookSettings}
                  />
                  <Route
                    path={paths.monitor.CONNECTOR_HEALTH}
                    component={ConnectorHealth}
                  />
                  <Route
                    path={paths.monitor.MANAGED_BANK_FEEDS_SYNCS}
                    component={ManagedBankFeedsSyncs}
                  />
                  <Route
                    path={paths.settings.OVERVIEW}
                    component={SettingsOverview}
                    exact
                  />
                  <AuthorizedRoute
                    path={paths.settings.LINK_SETTINGS}
                    permission="authFlow_view"
                    component={LinkSettings}
                  />
                  <AuthorizedRoute
                    path={paths.settings.BRANDING}
                    permission="clients_companyProfile_edit"
                    component={Branding}
                  />
                  <AuthorizedRoute
                    path={paths.settings.REDIRECTS}
                    permission="clients_companyProfile_edit"
                    component={Redirects}
                  />
                  <AuthorizedRoute
                    path={paths.settings.DATA_TYPES}
                    permission="clients_syncSettings_view"
                    component={SynchronizationSettings}
                  />
                  <Route exact path={paths.settings.USERS} component={Users} />
                  <Route
                    exact
                    path={paths.account.SETTINGS}
                    component={AccountSettings}
                  />
                  <TrackedRoute
                    path={paths.settings.PRODUCT_SETTINGS}
                    component={ProductSettings}
                    name="Product Settings Page"
                  />
                  <TrackedRoute
                    path={paths.settings.MANAGE_PRODUCTS}
                    component={Products}
                    name="Products Page"
                  />
                  <TrackedRoute
                    path={paths.settings.BILLING}
                    component={Billing}
                    name="Billing Page"
                  />
                  <CompatRoute
                    path={paths.developers.DEPRECATIONS}
                    component={Deprecations}
                  />
                  <Route path={paths.developers.API_KEYS} component={ApiKeys} />
                  <Route
                    path="/settings/integrations"
                    component={IntegrationsManagement}
                  />
                  <Route
                    path={paths.products.ACCOUNTING_API}
                    render={() => <ProductPages productName="Accounting" />}
                  />
                  <Route
                    path={paths.products.BANKING_API}
                    render={() => <ProductPages productName="Banking" />}
                  />
                  <Route
                    path={paths.products.COMMERCE_API}
                    render={() => <ProductPages productName="Commerce" />}
                  />
                  <Route component={NotFound} />
                </Switch>
              </Route>
            </Layout>
          </Switch>
        </ErrorBoundary>
      </AuthProvider>
    </Switch>
  );
}

export default AppRouter;
